import { safeRandomUUID } from "@/lib/utils"
import {
  supabase,
  isNotFoundError,
  probeAnalyticsTable,
  markAnalyticsTableUnavailable,
  getCachedAnalyticsAvailability,
  type AnalyticsTable,
} from "@/lib/supabase"
import { toast } from "@/hooks/use-toast"
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react"

type ProfileEngagementReturn = {
  profileId: string
  likes: number
  shares: number
  hasLiked: boolean
  likeBusy: boolean
  shareBusy: boolean
  like: () => Promise<void>
  share: () => Promise<boolean>
  refresh: () => Promise<void>
}

function probeTable(table: AnalyticsTable): Promise<"available" | "unavailable"> {
  return probeAnalyticsTable(supabase, table)
}

function isValidProfileId(profileId: string): boolean {
  const id = String(profileId || "")
  if (!id) return false
  if (id.length > 240) return false
  if (!/^pri:[a-z0-9:_-]+$/i.test(id)) return false
  return true
}

function nowMs() {
  return Date.now()
}

function rateLimited(lastAtRef: MutableRefObject<number>, minIntervalMs: number): boolean {
  const t = nowMs()
  if (t - lastAtRef.current < minIntervalMs) return true
  lastAtRef.current = t
  return false
}

export function useProfileEngagement(profileId: string, userId?: string | null): ProfileEngagementReturn {
  const pid = String(profileId || "")
  const uid = userId ?? null

  const [likes, setLikes] = useState(0)
  const [shares, setShares] = useState(0)
  const [hasLiked, setHasLiked] = useState(false)
  const [likeBusy, setLikeBusy] = useState(false)
  const [shareBusy, setShareBusy] = useState(false)

  const likeLastAtRef = useRef(0)
  const shareLastAtRef = useRef(0)
  const initialRefreshSettledRef = useRef(false)

  const canQuery = useMemo(() => isValidProfileId(pid), [pid])

  const refresh = useCallback(async () => {
    if (!canQuery) return

    if (!getCachedAnalyticsAvailability("profile_engagements")) {
      await probeAnalyticsTable(supabase, "profile_engagements")
    }
    if (!getCachedAnalyticsAvailability("profile_shares")) {
      await probeAnalyticsTable(supabase, "profile_shares")
    }

    const engAvail = getCachedAnalyticsAvailability("profile_engagements")
    const sharesAvail = getCachedAnalyticsAvailability("profile_shares")

    const engEnabled = engAvail === "available"
    const sharesEnabled = sharesAvail === "available"

    if (!engEnabled && !sharesEnabled) {
      setLikes(0)
      setShares(0)
      if (!uid) setHasLiked(false)
      return
    }

    const likeCountReq = engEnabled
      ? supabase
        .from("profile_engagements")
        .select("id")
        .eq("profile_id", pid)
        .eq("action_type", "like")
      : Promise.resolve({ data: [], error: null })

    const shareCountReq = sharesEnabled
      ? supabase
        .from("profile_shares")
        .select("share_id")
        .eq("profile_id", pid)
      : Promise.resolve({ data: [], error: null })

    const likedReq = uid && engEnabled
      ? supabase
        .from("profile_engagements")
        .select("id")
        .eq("profile_id", pid)
        .eq("user_id", uid)
        .eq("action_type", "like")
        .maybeSingle()
      : null

    try {
      const [likeCountRes, shareCountRes, likedRes] = await Promise.all([
        likeCountReq,
        shareCountReq,
        likedReq ?? Promise.resolve(null),
      ])

      if (!likeCountRes.error) setLikes(Array.isArray(likeCountRes.data) ? likeCountRes.data.length : 0)
      if (!shareCountRes.error) setShares(Array.isArray(shareCountRes.data) ? shareCountRes.data.length : 0)

      if (!uid) {
        setHasLiked(false)
        return
      }
      if (likedRes && !likedRes.error) setHasLiked(Boolean(likedRes.data))
    } catch (error) {
      console.error("[useProfileEngagement] refresh failed", { profileId: pid, userId: uid, error })
    }
  }, [canQuery, pid, uid])

  useEffect(() => {
    if (!canQuery) return
    if (initialRefreshSettledRef.current) return
    initialRefreshSettledRef.current = true
    refresh().catch((error) => {
      console.error("[useProfileEngagement] initial refresh failed", { profileId: pid, userId: uid, error })
    })
  }, [canQuery, refresh, pid, uid])

  useEffect(() => {
    if (!canQuery) return
    let cancelled = false
    let channel: ReturnType<typeof supabase.channel> | null = null

      ; (async () => {
        const engAvail = getCachedAnalyticsAvailability("profile_engagements")
        if (cancelled || engAvail !== "available") return

        channel = supabase
          .channel(`profile_engagements:${pid}`)
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "profile_engagements", filter: `profile_id=eq.${pid}` },
            () => {
              refresh().catch((error) => {
                console.error("[useProfileEngagement] realtime refresh failed", { profileId: pid, userId: uid, error })
              })
            },
          )
          .subscribe()
      })().catch((error) => {
        console.error("[useProfileEngagement] realtime setup failed", { profileId: pid, userId: uid, error })
      })

    return () => {
      cancelled = true
      if (channel != null) {
        supabase.removeChannel(channel)
        channel = null
      }
    }
  }, [canQuery, pid, refresh, uid])

  const like = useCallback(async () => {
    if (!canQuery) return
    if (likeBusy) return
    if (rateLimited(likeLastAtRef, 6000)) return

    if (!uid) {
      return
    }

    const engAvail = await probeTable("profile_engagements")
    if (engAvail !== "available") {
      toast({ description: "Likes unavailable" })
      return
    }

    setLikeBusy(true)
    try {
      const { data: existing, error: fetchError } = await supabase
        .from("profile_engagements")
        .select("id")
        .eq("profile_id", pid)
        .eq("user_id", uid)
        .eq("action_type", "like")
        .maybeSingle()

      if (fetchError) {
        if (isNotFoundError(fetchError)) {
          markAnalyticsTableUnavailable("profile_engagements")
        }
        toast({ variant: "destructive", description: "Like failed" })
        return
      }

      if (existing?.id) {
        console.log("[profile_engagements] BEFORE_DELETE", { profile_id: pid, user_id: uid, action_type: "like", id: existing.id })
        const { error } = await supabase
          .from("profile_engagements")
          .delete()
          .eq("id", existing.id)

        if (error) {
          if (isNotFoundError(error)) markAnalyticsTableUnavailable("profile_engagements")
          console.log("[profile_engagements] DELETE_ERROR", { profile_id: pid, user_id: uid, action_type: "like", id: existing.id, error })
          toast({ variant: "destructive", description: "Like failed" })
          return
        }
        console.log("[profile_engagements] AFTER_DELETE", { profile_id: pid, user_id: uid, action_type: "like", id: existing.id })
        setHasLiked(false)
      } else {
        console.log("[profile_engagements] BEFORE_INSERT", { profile_id: pid, user_id: uid, action_type: "like" })
        const { error } = await supabase.from("profile_engagements").insert({
          profile_id: pid,
          user_id: uid,
          action_type: "like",
        })

        if (error) {
          if (isNotFoundError(error)) markAnalyticsTableUnavailable("profile_engagements")
          console.log("[profile_engagements] INSERT_ERROR", { profile_id: pid, user_id: uid, action_type: "like", error })
          toast({ variant: "destructive", description: "Like failed" })
          return
        }
        console.log("[profile_engagements] AFTER_INSERT", { profile_id: pid, user_id: uid, action_type: "like" })
        setHasLiked(true)
      }

      await refresh()
    } finally {
      setLikeBusy(false)
    }
  }, [canQuery, likeBusy, pid, refresh, uid])

  const share = useCallback(async () => {
    if (!canQuery) return false
    if (shareBusy) return false
    if (rateLimited(shareLastAtRef, 6000)) return false

    setShareBusy(true)
    try {
      const shareId = safeRandomUUID()
      const shareLink = `${window.location.origin}/profile/${encodeURIComponent(pid)}?share_id=${shareId}`

      const sharesAvail = await probeTable("profile_shares")

      if (sharesAvail === "available") {
        console.log("[profile_shares] BEFORE_INSERT", { share_id: shareId, profile_id: pid, sharer_user_id: uid })
        const { error: insertError } = await supabase.from("profile_shares").insert({
          share_id: shareId,
          profile_id: pid,
          sharer_user_id: uid,
        })

        if (insertError) {
          if (isNotFoundError(insertError)) markAnalyticsTableUnavailable("profile_shares")
          console.log("[profile_shares] INSERT_ERROR", { share_id: shareId, profile_id: pid, sharer_user_id: uid, error: insertError })
          toast({ variant: "destructive", description: "Share failed" })
          return false
        }
        console.log("[profile_shares] AFTER_INSERT", { share_id: shareId, profile_id: pid, sharer_user_id: uid })
      }

      const message = `देखिए अपनी डिजिटल पंचायत 👇\n\n${shareLink}`
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`

      window.open(whatsappUrl, "_blank")

      try {
        const copyMessage = `देखिए अपनी डिजिटल पंचायत 👇\n\n${shareLink}`
        await navigator.clipboard.writeText(copyMessage)
        alert("Link copied:\n\n" + copyMessage)
        return true
      } catch {
        toast({ variant: "destructive", description: "Copy failed" })
        return false
      }
    } finally {
      setShareBusy(false)
    }
  }, [canQuery, pid, shareBusy, uid])

  return {
    profileId: pid,
    likes,
    shares,
    hasLiked,
    likeBusy,
    shareBusy,
    like,
    share,
    refresh,
  }
}

export async function trackShareOpen(shareId: string): Promise<void> {
  const sid = String(shareId || "")
  if (!sid) return
  if (sid.length > 128) return

  const analyticsAvail = await probeTable("share_analytics")
  if (analyticsAvail !== "available") return

  const FingerprintJS = (await import("@fingerprintjs/fingerprintjs")).default
  const fp = await FingerprintJS.load()
  const result = await fp.get()
  const fingerprint = result.visitorId

  console.log("[share_analytics] BEFORE_INSERT", { share_id: sid, device_fingerprint: fingerprint })
  const { error } = await supabase.from("share_analytics").insert({
    share_id: sid,
    device_fingerprint: fingerprint,
    user_agent: navigator.userAgent,
  })

  if (!error) {
    console.log("[share_analytics] AFTER_INSERT", { share_id: sid, device_fingerprint: fingerprint })
    return
  }
  if (isNotFoundError(error)) {
    markAnalyticsTableUnavailable("share_analytics")
    return
  }
  const code = typeof (error as unknown as { code?: unknown }).code === "string" ? (error as unknown as { code: string }).code : ""
  if (code === "23505" || code === "409") {
    console.log("Duplicate device - already counted")
    return
  }
  console.log("[share_analytics] INSERT_ERROR", { share_id: sid, device_fingerprint: fingerprint, error })
}
